import { useState, useEffect, useRef } from "react";

// ============================================================
// MOCK DATA & UTILITIES
// ============================================================
const PROPERTIES = [
  { id: 1, name: "Splash Kingdom Waterpark", type: "waterpark", city: "Mumbai", price: 799, originalPrice: 1199, rating: 4.8, reviews: 2341, image: "🌊", bg: "from-cyan-500 to-blue-600", tags: ["Family", "Adventure", "Slides"], available: 450, amenities: ["Parking", "Locker", "Food Court", "Wave Pool", "Kids Zone"], description: "Mumbai's largest waterpark with 25+ rides and attractions. Perfect for families and thrill seekers!", discount: 33, offers: ["SPLASH20 - 20% off", "GROUP10 - ₹100 off on 4+ tickets"], featured: true, latitude: 19.076, longitude: 72.877 },
  { id: 2, name: "Neon Nights Club", type: "nightclub", city: "Delhi", price: 1499, originalPrice: 1999, rating: 4.6, reviews: 1892, image: "🎵", bg: "from-purple-600 to-pink-600", tags: ["Music", "Dance", "Cocktails"], available: 120, amenities: ["VIP Section", "Open Bar", "DJ", "Smoking Area", "Valet"], description: "Delhi's most iconic nightclub featuring international DJs and premium cocktails.", discount: 25, offers: ["NEON15 - 15% off"], featured: true, latitude: 28.614, longitude: 77.209 },
  { id: 3, name: "Sky Lounge Bar", type: "bar", city: "Bangalore", price: 999, originalPrice: 1299, rating: 4.7, reviews: 3102, image: "🍸", bg: "from-amber-500 to-orange-600", tags: ["Rooftop", "Cocktails", "Live Music"], available: 80, amenities: ["Rooftop View", "Live Band", "Hookah", "Bite Menu"], description: "Bangalore's most stunning rooftop bar with 360° city views and craft cocktails.", discount: 23, offers: ["SKY10 - 10% off on weekdays"], featured: false, latitude: 12.971, longitude: 77.594 },
  { id: 4, name: "Warehouse Club", type: "club", city: "Mumbai", price: 1199, originalPrice: 1599, rating: 4.5, reviews: 987, image: "🔊", bg: "from-gray-800 to-black", tags: ["Electronic", "Techno", "Rave"], available: 200, amenities: ["Sound System", "Light Show", "Bar", "Cloakroom"], description: "Underground industrial vibes with cutting-edge sound systems and the best techno in the city.", discount: 25, offers: ["RAVE25 - 25% off first booking"], featured: true, latitude: 19.076, longitude: 72.877 },
  { id: 5, name: "The Social Kitchen", type: "restaurant", city: "Pune", price: 599, originalPrice: 799, rating: 4.9, reviews: 5421, image: "🍽️", bg: "from-green-500 to-teal-600", tags: ["Fine Dining", "Brunch", "Garden"], available: 60, amenities: ["Garden Seating", "Bar", "Live Music", "Valet", "Private Dining"], description: "A culinary experience in a lush garden setting. Farm-to-table cuisine with an extensive wine list.", discount: 25, offers: ["BRUNCH30 - 30% off Sunday brunch"], featured: false, latitude: 18.520, longitude: 73.856 },
  { id: 6, name: "Sunburn Arena", type: "event", city: "Goa", price: 2499, originalPrice: 3499, rating: 4.9, reviews: 8920, image: "🎪", bg: "from-yellow-400 to-orange-500", tags: ["Festival", "EDM", "Beach"], available: 5000, amenities: ["Camping", "Multiple Stages", "Food Village", "Art Zone"], description: "India's biggest music festival returns! 3 days of non-stop music on Goa's iconic beaches.", discount: 28, offers: ["EARLY500 - ₹500 early bird"], featured: true, latitude: 15.491, longitude: 73.832 },
];

const CATEGORIES = [
  { id: "all", label: "All", icon: "✨" },
  { id: "waterpark", label: "Waterpark", icon: "🌊" },
  { id: "nightclub", label: "Night Club", icon: "🎵" },
  { id: "club", label: "Club", icon: "🔊" },
  { id: "bar", label: "Bar", icon: "🍸" },
  { id: "restaurant", label: "Restaurant", icon: "🍽️" },
  { id: "event", label: "Events", icon: "🎪" },
];

const CITIES = ["Mumbai", "Delhi", "Bangalore", "Pune", "Goa", "Chennai", "Hyderabad"];

function generateQR(data) {
  // Simple visual QR-like pattern based on hash
  const hash = data.split("").reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0);
  const cells = 21;
  const grid = Array(cells).fill(null).map((_, i) =>
    Array(cells).fill(null).map((_, j) => {
      if ((i < 7 && j < 7) || (i < 7 && j > 13) || (i > 13 && j < 7)) return 1;
      if (i === 6 || j === 6 || (i > 7 && i < 12 && j > 7 && j < 12)) return Math.abs((hash * (i + 1) * (j + 1)) % 3) < 1 ? 1 : 0;
      return Math.abs((hash * i + j * 7 + i * j) % 5) < 2 ? 1 : 0;
    })
  );
  return grid;
}

function formatCurrency(n) { return "₹" + n.toLocaleString("en-IN"); }

// ============================================================
// COMPONENTS
// ============================================================

function QRCode({ data, size = 160 }) {
  const grid = generateQR(data);
  const cell = size / grid.length;
  return (
    <svg width={size} height={size} style={{ display: "block" }}>
      <rect width={size} height={size} fill="white" />
      {grid.map((row, i) => row.map((val, j) => val ? (
        <rect key={`${i}-${j}`} x={j * cell} y={i * cell} width={cell} height={cell} fill="#1a1a2e" />
      ) : null))}
    </svg>
  );
}

function ScratchCard({ onReveal, prize }) {
  const canvasRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [scratching, setScratching] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#6366f1";
    ctx.fillRect(0, 0, 200, 100);
    ctx.fillStyle = "rgba(255,255,255,0.2)";
    for (let i = 0; i < 20; i++) {
      ctx.fillRect(Math.random() * 200, Math.random() * 100, 20, 4);
    }
    ctx.fillStyle = "white";
    ctx.font = "bold 14px system-ui";
    ctx.textAlign = "center";
    ctx.fillText("✨ SCRATCH ME!", 100, 55);
  }, []);

  const scratch = (e) => {
    if (revealed) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, 18, 0, Math.PI * 2);
    ctx.fill();
    const newProgress = Math.min(progress + 5, 100);
    setProgress(newProgress);
    if (newProgress >= 60 && !revealed) {
      setRevealed(true);
      onReveal && onReveal(prize);
    }
  };

  return (
    <div style={{ position: "relative", width: 200, height: 100, borderRadius: 12, overflow: "hidden", cursor: "crosshair" }}>
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #ffd700, #ffaa00)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
        <div style={{ fontSize: 24 }}>🎉</div>
        <div style={{ fontWeight: 800, fontSize: 18, color: "#1a1a2e" }}>{prize}</div>
        <div style={{ fontSize: 11, color: "#333" }}>Added to wallet!</div>
      </div>
      <canvas ref={canvasRef} width={200} height={100} style={{ position: "absolute", inset: 0, touchAction: "none" }}
        onMouseMove={e => scratching && scratch(e)}
        onMouseDown={() => setScratching(true)}
        onMouseUp={() => setScratching(false)}
        onTouchMove={scratch}
      />
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function AmuzingApp() {
  const [screen, setScreen] = useState("splash");
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("user"); // user | admin | superadmin
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [cart, setCart] = useState({ tickets: 1, date: "", coupon: "", walletUse: false });
  const [wallet, setWallet] = useState(320);
  const [tickets, setTickets] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeTab, setActiveTab] = useState("home");
  const [filter, setFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [showScratch, setShowScratch] = useState(false);
  const [scratchPrize, setScratchPrize] = useState("₹25 Cashback");
  const [loginForm, setLoginForm] = useState({ phone: "", otp: "", step: 1 });
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [adminTab, setAdminTab] = useState("overview");
  const [shareModal, setShareModal] = useState(false);
  const [paySuccess, setPaySuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Splash → Onboarding
  useEffect(() => {
    setTimeout(() => setScreen("onboarding"), 2200);
  }, []);

  const addNotification = (msg) => {
    const id = Date.now();
    setNotifications(p => [{ id, msg, time: new Date() }, ...p]);
  };

  const login = () => {
    setUser({ name: "Arjun Mehta", phone: loginForm.phone || "9876543210", avatar: "AM", walletBalance: wallet });
    setScreen("app");
    setActiveTab("home");
    addNotification("Welcome back! 🎉 ₹50 bonus added to wallet");
    setWallet(w => w + 50);
  };

  const loginAsAdmin = () => {
    setRole("admin");
    setUser({ name: "Venue Admin", phone: "9999999999", avatar: "VA" });
    setScreen("app");
    setActiveTab("admin");
  };

  const loginAsSuperAdmin = () => {
    setRole("superadmin");
    setUser({ name: "Super Admin", phone: "0000000000", avatar: "SA" });
    setScreen("app");
    setActiveTab("superadmin");
  };

  const bookNow = (prop) => {
    setSelectedProperty(prop);
    setCart({ tickets: 1, date: new Date().toISOString().split("T")[0], coupon: "", walletUse: false });
    setCouponApplied(null);
    setScreen("booking");
  };

  const calcPrice = () => {
    if (!selectedProperty) return {};
    const base = selectedProperty.price * cart.tickets;
    const platform = 0; // free
    const gst = Math.round(base * 0.18);
    let discount = couponApplied ? Math.round(base * (couponApplied.pct / 100)) : 0;
    const subtotal = base - discount + platform + gst;
    const maxWallet = Math.round(subtotal * 0.1);
    const walletUsed = cart.walletUse ? Math.min(wallet, maxWallet) : 0;
    const total = subtotal - walletUsed;
    return { base, platform, gst, discount, subtotal, walletUsed, total, maxWallet };
  };

  const confirmPayment = () => {
    const { total, walletUsed } = calcPrice();
    const cashback = Math.round(total * 0.05);
    const ticketId = "AMZ" + Date.now().toString().slice(-8);
    const newTicket = {
      id: ticketId,
      property: selectedProperty,
      date: cart.date,
      tickets: cart.tickets,
      total,
      cashback,
      qr: ticketId + "|" + selectedProperty.id + "|" + cart.date + "|" + cart.tickets,
      status: "active",
      bookedAt: new Date(),
    };
    setTickets(p => [newTicket, ...p]);
    setWallet(w => w - walletUsed + cashback);
    setPaySuccess(true);
    setScratchPrize("₹" + (Math.floor(Math.random() * 4) + 1) * 25 + " Cashback");
    setTimeout(() => { setShowScratch(true); }, 1200);
    addNotification(`Ticket booked! ₹${cashback} cashback added 🎊`);
  };

  const applyCoupon = () => {
    const coupons = { "SPLASH20": { pct: 20, label: "20% OFF" }, "NEON15": { pct: 15, label: "15% OFF" }, "SAVE10": { pct: 10, label: "10% OFF" } };
    if (coupons[couponInput.toUpperCase()]) {
      setCouponApplied(coupons[couponInput.toUpperCase()]);
    } else {
      addNotification("Invalid coupon code ❌");
    }
  };

  const validateQR = (qrData) => {
    const parts = qrData.split("|");
    const found = tickets.find(t => t.id === parts[0]);
    if (found) {
      setScanResult({ valid: true, ticket: found });
    } else {
      setScanResult({ valid: false, msg: "Invalid or expired ticket" });
    }
  };

  const simulateScan = () => {
    setScanning(true);
    setTimeout(() => {
      if (tickets.length > 0) {
        validateQR(tickets[0].qr);
      } else {
        setScanResult({ valid: false, msg: "No valid ticket found" });
      }
      setScanning(false);
    }, 2000);
  };

  const filteredProps = PROPERTIES.filter(p => {
    const matchCat = filter === "all" || p.type === filter;
    const matchCity = cityFilter === "all" || p.city === cityFilter;
    const matchSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.city.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchCity && matchSearch;
  });

  const prices = calcPrice();

  // ============================================================
  // SCREENS
  // ============================================================

  if (screen === "splash") return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <style>{`
        @keyframes pulse { 0%,100%{transform:scale(1)}50%{transform:scale(1.08)} }
        @keyframes float { 0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)} }
        @keyframes glow { 0%,100%{box-shadow:0 0 20px #a855f7}50%{box-shadow:0 0 40px #ec4899,0 0 60px #a855f7} }
        @keyframes confetti { 0%{opacity:1;transform:translateY(0) rotate(0)}100%{opacity:0;transform:translateY(100vh) rotate(720deg)} }
        @keyframes shimmer { 0%{background-position:-200%}100%{background-position:200%} }
        @keyframes bounce { 0%,100%{transform:translateY(0)}40%{transform:translateY(-8px)} }
      `}</style>
      <div style={{ animation: "float 3s ease-in-out infinite", fontSize: 80, marginBottom: 20 }}>🎡</div>
      <div style={{ fontSize: 42, fontWeight: 900, color: "white", letterSpacing: -1, animation: "pulse 2s infinite" }}>
        <span style={{ color: "#f59e0b" }}>a</span>muzing
      </div>
      <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginTop: 8, letterSpacing: 3, textTransform: "uppercase" }}>Life's too short to be bored</div>
      <div style={{ marginTop: 40, width: 40, height: 40, border: "3px solid transparent", borderTop: "3px solid #f59e0b", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    </div>
  );

  if (screen === "onboarding") return (
    <div style={{ minHeight: "100vh", background: "#0f0c29", fontFamily: "'Segoe UI', system-ui, sans-serif", overflowX: "hidden" }}>
      <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(40px)}to{opacity:1;transform:translateY(0)}}@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}@keyframes pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.05)}}`}</style>
      <div style={{ background: "linear-gradient(180deg, #1a0533 0%, #0f0c29 100%)", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        {/* Hero */}
        <div style={{ padding: "60px 24px 0", textAlign: "center" }}>
          <div style={{ fontSize: 72, animation: "float 3s ease-in-out infinite", display: "inline-block", marginBottom: 8 }}>🎡</div>
          <div style={{ fontSize: 38, fontWeight: 900, color: "white", lineHeight: 1.1, animation: "slideUp 0.8s ease both" }}>
            <span style={{ color: "#f59e0b" }}>a</span>muzing
          </div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, letterSpacing: 3, marginTop: 4 }}>BOOK • VIBE • EXPLORE</div>
          <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 17, marginTop: 20, lineHeight: 1.6, animation: "slideUp 0.9s 0.1s ease both", opacity: 0, animationFillMode: "forwards" }}>
            Waterparks, Clubs, Bars, Restaurants & Events — all in one place ✨
          </div>
        </div>

        {/* Feature Pills */}
        <div style={{ display: "flex", gap: 8, padding: "24px 20px", overflowX: "auto", scrollbarWidth: "none" }}>
          {[["🎟️", "Instant Tickets"], ["💰", "5% Cashback"], ["🎰", "Scratch Cards"], ["👯", "Refer & Earn ₹50"], ["📱", "QR Entry"]].map(([icon, text]) => (
            <div key={text} style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: "8px 14px", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
              <span>{icon}</span>
              <span style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, fontWeight: 600 }}>{text}</span>
            </div>
          ))}
        </div>

        {/* Login Section */}
        <div style={{ flex: 1, padding: "0 20px 40px" }}>
          <div style={{ background: "rgba(255,255,255,0.05)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 24, padding: 24 }}>
            <div style={{ color: "white", fontSize: 20, fontWeight: 800, marginBottom: 20 }}>Get Started 🚀</div>

            {loginForm.step === 1 ? (
              <>
                <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 16 }}>🇮🇳 +91</span>
                  <input placeholder="Mobile Number" style={{ background: "none", border: "none", outline: "none", color: "white", fontSize: 16, flex: 1 }}
                    value={loginForm.phone} onChange={e => setLoginForm(p => ({ ...p, phone: e.target.value }))} />
                </div>
                <button onClick={() => setLoginForm(p => ({ ...p, step: 2 }))} style={{ width: "100%", padding: "16px", background: "linear-gradient(135deg, #f59e0b, #ef4444)", border: "none", borderRadius: 14, color: "white", fontSize: 16, fontWeight: 800, cursor: "pointer" }}>
                  Get OTP 📲
                </button>
              </>
            ) : (
              <>
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginBottom: 12 }}>OTP sent to {loginForm.phone} ✓</div>
                <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                  {[0,1,2,3].map(i => (
                    <div key={i} style={{ flex: 1, background: "rgba(255,255,255,0.08)", borderRadius: 12, padding: "16px", textAlign: "center", color: "white", fontSize: 20, fontWeight: 800, border: "1px solid rgba(245,158,11,0.4)" }}>
                      {loginForm.otp[i] || ""}
                    </div>
                  ))}
                </div>
                <input placeholder="Enter OTP (use 1234)" style={{ width: "100%", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 14, padding: "14px 16px", color: "white", fontSize: 16, marginBottom: 14, boxSizing: "border-box" }}
                  value={loginForm.otp} onChange={e => setLoginForm(p => ({ ...p, otp: e.target.value }))} />
                <button onClick={login} style={{ width: "100%", padding: "16px", background: "linear-gradient(135deg, #f59e0b, #ef4444)", border: "none", borderRadius: 14, color: "white", fontSize: 16, fontWeight: 800, cursor: "pointer" }}>
                  Verify & Enter 🎉
                </button>
              </>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={loginAsAdmin} style={{ flex: 1, padding: "12px", background: "rgba(139,92,246,0.2)", border: "1px solid rgba(139,92,246,0.4)", borderRadius: 12, color: "#a78bfa", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Admin Login
              </button>
              <button onClick={loginAsSuperAdmin} style={{ flex: 1, padding: "12px", background: "rgba(239,68,68,0.2)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 12, color: "#fca5a5", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                Super Admin
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // MAIN APP SHELL
  // ============================================================
  const S = { // styles
    shell: { minHeight: "100vh", background: "#0a0a0f", fontFamily: "'Segoe UI', system-ui, sans-serif", maxWidth: 430, margin: "0 auto", position: "relative", overflowX: "hidden" },
    topBar: { background: "rgba(15,12,41,0.95)", backdropFilter: "blur(20px)", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, borderBottom: "1px solid rgba(255,255,255,0.06)" },
    bottomNav: { position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 430, background: "rgba(15,12,41,0.97)", backdropFilter: "blur(20px)", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", zIndex: 100 },
    navItem: (active) => ({ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "10px 0", cursor: "pointer", transition: "all 0.2s", color: active ? "#f59e0b" : "rgba(255,255,255,0.4)", fontSize: 10, fontWeight: active ? 700 : 500, gap: 4, borderTop: active ? "2px solid #f59e0b" : "2px solid transparent" }),
    card: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, overflow: "hidden", marginBottom: 14 },
    pill: (active, color = "#f59e0b") => ({ padding: "8px 16px", borderRadius: 20, cursor: "pointer", whiteSpace: "nowrap", fontSize: 13, fontWeight: 700, background: active ? color : "rgba(255,255,255,0.06)", color: active ? "#0a0a0f" : "rgba(255,255,255,0.6)", border: active ? "none" : "1px solid rgba(255,255,255,0.08)", transition: "all 0.2s" }),
    btn: (variant = "primary") => ({ padding: "16px 24px", borderRadius: 14, border: "none", cursor: "pointer", fontWeight: 800, fontSize: 15, transition: "all 0.2s", background: variant === "primary" ? "linear-gradient(135deg, #f59e0b, #ef4444)" : variant === "ghost" ? "rgba(255,255,255,0.07)" : "#1e1e2e", color: "white", width: "100%" }),
    input: { width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "13px 16px", color: "white", fontSize: 15, boxSizing: "border-box", outline: "none" },
    sectionTitle: { color: "white", fontSize: 20, fontWeight: 800, marginBottom: 16 },
    tag: (color = "#f59e0b") => ({ background: color + "22", color, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 8, display: "inline-block" }),
  };

  const navItems = role === "superadmin"
    ? [["🏠", "Dashboard", "superadmin"], ["🏢", "Venues", "venues"], ["👥", "Users", "users_list"], ["📊", "Analytics", "analytics"]]
    : role === "admin"
    ? [["📊", "Overview", "admin"], ["🏢", "My Venue", "my_venue"], ["🎟️", "Tickets", "scan"], ["⚙️", "Settings", "settings"]]
    : [["🏠", "Home", "home"], ["🗺️", "Explore", "explore"], ["🎟️", "My Tickets", "tickets"], ["👛", "Wallet", "wallet"], ["👤", "Profile", "profile"]];

  const renderHome = () => (
    <div style={{ padding: "0 0 80px" }}>
      {/* Hero Banner */}
      <div style={{ background: "linear-gradient(135deg, #1a0533, #302b63)", padding: "20px 20px 24px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 150, height: 150, background: "radial-gradient(circle, rgba(245,158,11,0.3), transparent)", borderRadius: "50%" }} />
        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginBottom: 4 }}>Good evening, {user?.name?.split(" ")[0]} 👋</div>
        <div style={{ color: "white", fontSize: 22, fontWeight: 900, marginBottom: 16, lineHeight: 1.2 }}>Where are you vibing tonight? 🔥</div>

        {/* Search */}
        <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🔍</span>
          <input placeholder="Search venues, events, cities..." style={{ background: "none", border: "none", outline: "none", color: "white", fontSize: 14, flex: 1 }}
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>

        {/* Wallet Banner */}
        <div style={{ marginTop: 14, background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>Wallet Balance</div>
            <div style={{ color: "#f59e0b", fontSize: 20, fontWeight: 900 }}>₹{wallet}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>Refer & Earn</div>
            <button onClick={() => setShareModal(true)} style={{ background: "linear-gradient(135deg, #f59e0b, #ef4444)", border: "none", borderRadius: 10, padding: "6px 12px", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>₹50 Bonus 🎁</button>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div style={{ padding: "20px 0 8px" }}>
        <div style={{ display: "flex", gap: 8, padding: "0 20px", overflowX: "auto", scrollbarWidth: "none" }}>
          {CATEGORIES.map(c => (
            <button key={c.id} onClick={() => setFilter(c.id)} style={S.pill(filter === c.id)}>
              {c.icon} {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* City Filter */}
      <div style={{ padding: "8px 0" }}>
        <div style={{ display: "flex", gap: 8, padding: "0 20px", overflowX: "auto", scrollbarWidth: "none" }}>
          <button onClick={() => setCityFilter("all")} style={S.pill(cityFilter === "all", "#6366f1")}>🌍 All Cities</button>
          {CITIES.map(c => (
            <button key={c} onClick={() => setCityFilter(c)} style={S.pill(cityFilter === c, "#6366f1")}>{c}</button>
          ))}
        </div>
      </div>

      {/* Featured Banner */}
      {filter === "all" && (
        <div style={{ padding: "16px 20px 0" }}>
          <div style={{ background: "linear-gradient(135deg, #f59e0b22, #ef444422)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 20, padding: "16px", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ fontSize: 36 }}>🎉</div>
            <div>
              <div style={{ color: "#f59e0b", fontSize: 12, fontWeight: 700 }}>WEEKEND SPECIAL</div>
              <div style={{ color: "white", fontSize: 15, fontWeight: 800 }}>Sunburn Arena — Early Bird ₹500 off!</div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12, marginTop: 2 }}>Only 312 tickets left</div>
            </div>
          </div>
        </div>
      )}

      {/* Properties */}
      <div style={{ padding: "20px" }}>
        <div style={S.sectionTitle}>{filter === "all" ? "🔥 Trending Near You" : CATEGORIES.find(c => c.id === filter)?.icon + " " + CATEGORIES.find(c => c.id === filter)?.label}</div>
        {filteredProps.length === 0 ? (
          <div style={{ textAlign: "center", color: "rgba(255,255,255,0.4)", padding: "40px 0" }}>No venues found 😔</div>
        ) : filteredProps.map(p => (
          <div key={p.id} style={S.card}>
            {/* Image/Banner */}
            <div style={{ background: `linear-gradient(135deg, var(--s), var(--e))`, backgroundImage: `linear-gradient(135deg, ${p.bg.replace("from-", "").replace("to-", "").split(" ").join(", ")})`, height: 160, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 64, position: "relative" }}>
              {p.image}
              {p.featured && <div style={{ position: "absolute", top: 12, left: 12, background: "#f59e0b", borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 800, color: "#1a1a2e" }}>⭐ FEATURED</div>}
              <div style={{ position: "absolute", top: 12, right: 12, background: "#ef4444", borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 800, color: "white" }}>{p.discount}% OFF</div>
              <div style={{ position: "absolute", bottom: 12, right: 12, background: "rgba(0,0,0,0.6)", borderRadius: 8, padding: "4px 10px", fontSize: 12, color: "white" }}>📍 {p.city}</div>
            </div>

            <div style={{ padding: "16px" }}>
              <div style={{ display: "flex", alignItems: "start", justifyContent: "space-between", marginBottom: 8 }}>
                <div>
                  <div style={{ color: "white", fontSize: 17, fontWeight: 800 }}>{p.name}</div>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, marginTop: 2 }}>⭐ {p.rating} ({p.reviews.toLocaleString()} reviews)</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "#f59e0b", fontSize: 20, fontWeight: 900 }}>₹{p.price}</div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, textDecoration: "line-through" }}>₹{p.originalPrice}</div>
                </div>
              </div>

              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>{p.description}</div>

              {/* Tags */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {p.tags.map(t => <span key={t} style={S.tag()}>{t}</span>)}
              </div>

              {/* Amenities */}
              <div style={{ display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none", marginBottom: 14 }}>
                {p.amenities.slice(0, 4).map(a => (
                  <div key={a} style={{ background: "rgba(255,255,255,0.05)", borderRadius: 8, padding: "5px 10px", fontSize: 11, color: "rgba(255,255,255,0.6)", whiteSpace: "nowrap" }}>✓ {a}</div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => bookNow(p)} style={{ ...S.btn(), flex: 2 }}>Book Now 🎟️</button>
                <button style={{ ...S.btn("ghost"), flex: 1, padding: "16px 0" }}>❤️ Save</button>
              </div>

              <div style={{ marginTop: 10, background: "rgba(34,197,94,0.1)", borderRadius: 10, padding: "8px 12px", fontSize: 12, color: "#4ade80", display: "flex", gap: 6 }}>
                💚 5% cashback on this booking! Get ₹{Math.round(p.price * 0.05)} back in wallet
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderBooking = () => {
    const p = calcPrice();
    return (
      <div style={{ padding: "0 0 100px" }}>
        {/* Back */}
        <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => setScreen("app")} style={{ background: "rgba(255,255,255,0.08)", border: "none", borderRadius: 10, padding: "8px 12px", color: "white", cursor: "pointer", fontSize: 18 }}>←</button>
          <div style={{ color: "white", fontSize: 18, fontWeight: 800 }}>Book Tickets</div>
        </div>

        <div style={{ padding: "0 20px" }}>
          {/* Venue Info */}
          <div style={S.card}>
            <div style={{ background: `linear-gradient(135deg, #1a0533, #302b63)`, padding: "20px", display: "flex", gap: 14, alignItems: "center" }}>
              <div style={{ fontSize: 48 }}>{selectedProperty?.image}</div>
              <div>
                <div style={{ color: "white", fontSize: 18, fontWeight: 800 }}>{selectedProperty?.name}</div>
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>📍 {selectedProperty?.city}</div>
                <div style={{ color: "#f59e0b", fontSize: 13, fontWeight: 700, marginTop: 4 }}>⭐ {selectedProperty?.rating} · {selectedProperty?.available} tickets available</div>
              </div>
            </div>
          </div>

          {/* Date Picker */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>📅 Select Date</div>
            <input type="date" value={cart.date} onChange={e => setCart(c => ({ ...c, date: e.target.value }))} min={new Date().toISOString().split("T")[0]}
              style={{ ...S.input, colorScheme: "dark" }} />
          </div>

          {/* Ticket Count */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>🎟️ Number of Tickets</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {[1,2,3,4,5,6,8,10].map(n => (
                <button key={n} onClick={() => setCart(c => ({ ...c, tickets: n }))} style={{ width: 44, height: 44, borderRadius: 12, border: "none", cursor: "pointer", fontWeight: 800, fontSize: 15, background: cart.tickets === n ? "linear-gradient(135deg, #f59e0b, #ef4444)" : "rgba(255,255,255,0.06)", color: cart.tickets === n ? "white" : "rgba(255,255,255,0.6)" }}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Coupon Code */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>🏷️ Coupon Code</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input placeholder="Enter coupon (try SPLASH20)" style={{ ...S.input, flex: 1 }} value={couponInput} onChange={e => setCouponInput(e.target.value)} />
              <button onClick={applyCoupon} style={{ background: "rgba(99,102,241,0.3)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: 12, padding: "0 16px", color: "#a78bfa", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>Apply</button>
            </div>
            {couponApplied && <div style={{ color: "#4ade80", fontSize: 13, marginTop: 6 }}>✅ {couponApplied.label} applied!</div>}
          </div>

          {/* Price Breakdown */}
          <div style={{ ...S.card, padding: "20px" }}>
            <div style={{ color: "white", fontSize: 16, fontWeight: 800, marginBottom: 16 }}>💳 Price Breakdown</div>
            {[
              ["Ticket Price", `${cart.tickets} × ₹${selectedProperty?.price}`, "₹" + (selectedProperty?.price * cart.tickets)],
              ["Platform Fees", "FREE 🎉 (Usually ₹99)", "₹0"],
              ...(couponApplied ? [["Coupon Discount", couponApplied.label, `-₹${p.discount}`]] : []),
              ["GST (18%)", "As applicable", "₹" + p.gst],
            ].map(([label, sub, val]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }}>{label}</div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>{sub}</div>
                </div>
                <div style={{ color: val.startsWith("-") ? "#4ade80" : "rgba(255,255,255,0.9)", fontWeight: 700 }}>{val}</div>
              </div>
            ))}

            {/* Wallet Toggle */}
            {wallet > 0 && (
              <div style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div>
                  <div style={{ color: "#f59e0b", fontSize: 14, fontWeight: 700 }}>💛 Use Wallet Balance</div>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>Max 10% of total • Balance: ₹{wallet}</div>
                  {cart.walletUse && <div style={{ color: "#4ade80", fontSize: 12 }}>-₹{p.walletUsed} will be deducted</div>}
                </div>
                <div onClick={() => setCart(c => ({ ...c, walletUse: !c.walletUse }))} style={{ width: 48, height: 26, borderRadius: 13, background: cart.walletUse ? "#f59e0b" : "rgba(255,255,255,0.1)", position: "relative", cursor: "pointer", transition: "background 0.2s" }}>
                  <div style={{ width: 20, height: 20, borderRadius: "50%", background: "white", position: "absolute", top: 3, left: cart.walletUse ? 24 : 4, transition: "left 0.2s" }} />
                </div>
              </div>
            )}

            <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "12px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ color: "white", fontSize: 16, fontWeight: 800 }}>Total</div>
              <div style={{ color: "#f59e0b", fontSize: 24, fontWeight: 900 }}>₹{p.total}</div>
            </div>
            <div style={{ color: "#4ade80", fontSize: 13, marginTop: 8 }}>🎁 You'll earn ₹{Math.round(p.total * 0.05)} cashback + scratch card!</div>
          </div>

          <button onClick={confirmPayment} style={S.btn()}>
            Pay ₹{p.total} & Book 🎟️
          </button>
        </div>
      </div>
    );
  };

  const renderTickets = () => (
    <div style={{ padding: "20px 20px 80px" }}>
      <div style={S.sectionTitle}>🎟️ My Tickets</div>
      {tickets.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🎫</div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 16 }}>No tickets yet</div>
          <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 14, marginTop: 4 }}>Book your first experience!</div>
          <button onClick={() => setActiveTab("home")} style={{ ...S.btn(), marginTop: 20, width: "auto", padding: "14px 24px" }}>Explore Venues 🔥</button>
        </div>
      ) : tickets.map(t => (
        <div key={t.id} style={{ ...S.card, padding: 0 }}>
          <div style={{ background: `linear-gradient(135deg, #1a0533, #302b63)`, padding: "16px", display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ fontSize: 40 }}>{t.property.image}</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: "white", fontWeight: 800, fontSize: 15 }}>{t.property.name}</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>📅 {t.date} · {t.tickets} ticket{t.tickets > 1 ? "s" : ""}</div>
              <div style={{ color: "#f59e0b", fontSize: 13, fontWeight: 700 }}>₹{t.total} paid</div>
            </div>
            <div style={S.tag("#4ade80")}>{t.status.toUpperCase()}</div>
          </div>

          {/* QR Code */}
          <div style={{ padding: "20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <div style={{ padding: 12, background: "white", borderRadius: 16 }}>
              <QRCode data={t.qr} size={160} />
            </div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, fontFamily: "monospace" }}>{t.id}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 8, padding: "6px 12px", fontSize: 12, color: "rgba(255,255,255,0.6)" }}>📧 Sent to email</div>
              <div style={{ background: "rgba(37,211,102,0.15)", borderRadius: 8, padding: "6px 12px", fontSize: 12, color: "#4ade80" }}>💬 Sent to WhatsApp</div>
            </div>
            <div style={{ color: "#4ade80", fontSize: 13 }}>🎁 ₹{t.cashback} cashback earned!</div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderWallet = () => (
    <div style={{ padding: "20px 20px 80px" }}>
      {/* Wallet Card */}
      <div style={{ background: "linear-gradient(135deg, #1a0533, #302b63)", borderRadius: 24, padding: "28px", marginBottom: 20, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 140, height: 140, background: "radial-gradient(circle, rgba(245,158,11,0.4), transparent)", borderRadius: "50%" }} />
        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginBottom: 4 }}>💛 Wallet Balance</div>
        <div style={{ color: "#f59e0b", fontSize: 48, fontWeight: 900 }}>₹{wallet}</div>
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, marginTop: 4 }}>Available to use on your next booking</div>
      </div>

      {/* How it works */}
      <div style={S.sectionTitle}>How to earn more 🚀</div>
      {[
        ["🎟️", "Book Tickets", "5% cashback on every booking"],
        ["👯", "Refer Friends", "₹50 when they book their first ticket"],
        ["🎰", "Scratch Cards", "Win extra cashback on every booking"],
        ["💰", "Wallet Use", "Up to 10% of any ticket price"],
      ].map(([icon, title, desc]) => (
        <div key={title} style={{ display: "flex", gap: 14, alignItems: "center", padding: "14px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ width: 44, height: 44, borderRadius: 14, background: "rgba(245,158,11,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{icon}</div>
          <div>
            <div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>{title}</div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>{desc}</div>
          </div>
        </div>
      ))}

      {/* Refer Card */}
      <div style={{ marginTop: 20, background: "linear-gradient(135deg, rgba(245,158,11,0.2), rgba(239,68,68,0.2))", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 20, padding: 20 }}>
        <div style={{ color: "white", fontSize: 18, fontWeight: 800, marginBottom: 8 }}>🎁 Refer & Earn</div>
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, marginBottom: 16 }}>Share your code. When your friend books their first ticket, you BOTH get ₹50!</div>
        <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 12, padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div style={{ color: "#f59e0b", fontSize: 18, fontWeight: 900, fontFamily: "monospace", letterSpacing: 2 }}>AMZ{user?.phone?.slice(-4)}</div>
          <button style={{ background: "rgba(245,158,11,0.2)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 8, padding: "6px 14px", color: "#f59e0b", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Copy</button>
        </div>
        <button onClick={() => setShareModal(true)} style={S.btn()}>Share Now 📲</button>
      </div>
    </div>
  );

  const renderProfile = () => (
    <div style={{ padding: "20px 20px 80px" }}>
      <div style={{ background: "linear-gradient(135deg, #1a0533, #302b63)", borderRadius: 24, padding: "24px", marginBottom: 20, display: "flex", gap: 16, alignItems: "center" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg, #f59e0b, #ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 800, color: "white" }}>{user?.avatar}</div>
        <div>
          <div style={{ color: "white", fontSize: 20, fontWeight: 800 }}>{user?.name}</div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>+91 {user?.phone}</div>
          <div style={{ color: "#f59e0b", fontSize: 13, fontWeight: 700, marginTop: 4 }}>💛 ₹{wallet} wallet balance</div>
        </div>
      </div>

      {[
        ["🎟️", "My Bookings", tickets.length + " tickets"],
        ["💛", "Wallet & Rewards", "₹" + wallet + " balance"],
        ["🔔", "Notifications", notifications.length + " new"],
        ["👯", "Refer & Earn", "Earn ₹50 per referral"],
        ["⚙️", "Settings", "Account preferences"],
        ["❓", "Help & Support", "24/7 chat support"],
      ].map(([icon, title, sub]) => (
        <div key={title} style={{ display: "flex", gap: 14, alignItems: "center", padding: "16px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", cursor: "pointer" }}>
          <div style={{ width: 44, height: 44, borderRadius: 14, background: "rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ color: "white", fontWeight: 700 }}>{title}</div>
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13 }}>{sub}</div>
          </div>
          <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 18 }}>›</div>
        </div>
      ))}

      <button onClick={() => { setUser(null); setRole("user"); setScreen("onboarding"); }} style={{ ...S.btn("ghost"), marginTop: 20, color: "#ef4444" }}>Log Out</button>
    </div>
  );

  const renderScan = () => (
    <div style={{ padding: "20px 20px 80px" }}>
      <div style={S.sectionTitle}>📷 Ticket Scanner</div>

      <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 20, padding: 20, marginBottom: 20, border: "1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ background: "#1a1a2e", borderRadius: 16, height: 240, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, position: "relative", overflow: "hidden" }}>
          {scanning ? (
            <>
              <div style={{ width: 180, height: 180, border: "3px solid #f59e0b", borderRadius: 16, position: "relative" }}>
                <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: 3, background: "#f59e0b", animation: "slideDown 1.5s ease-in-out infinite" }} />
                <style>{"@keyframes slideDown{0%{top:0}50%{top:177px}100%{top:0}}"}</style>
              </div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>Scanning...</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 48 }}>📷</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>Point camera at QR code</div>
            </>
          )}
        </div>
        <button onClick={simulateScan} style={{ ...S.btn(), marginTop: 14 }}>
          {scanning ? "Scanning..." : "Simulate Scan 📷"}
        </button>
      </div>

      {scanResult && (
        <div style={{ background: scanResult.valid ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${scanResult.valid ? "#4ade80" : "#ef4444"}`, borderRadius: 20, padding: 20 }}>
          <div style={{ fontSize: 40, textAlign: "center", marginBottom: 12 }}>{scanResult.valid ? "✅" : "❌"}</div>
          <div style={{ color: "white", fontSize: 18, fontWeight: 800, textAlign: "center", marginBottom: 8 }}>
            {scanResult.valid ? "Valid Ticket!" : "Invalid Ticket"}
          </div>
          {scanResult.valid && scanResult.ticket && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                ["Venue", scanResult.ticket.property.name],
                ["Date", scanResult.ticket.date],
                ["Tickets", scanResult.ticket.tickets],
                ["Ticket ID", scanResult.ticket.id],
                ["Amount Paid", "₹" + scanResult.ticket.total],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 14 }}>{k}</span>
                  <span style={{ color: "white", fontSize: 14, fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
          )}
          {!scanResult.valid && <div style={{ color: "rgba(255,255,255,0.6)", textAlign: "center" }}>{scanResult.msg}</div>}
          <button onClick={() => setScanResult(null)} style={{ ...S.btn(), marginTop: 16 }}>Scan Another</button>
        </div>
      )}
    </div>
  );

  const renderAdminDashboard = () => (
    <div style={{ padding: "20px 20px 80px" }}>
      <div style={S.sectionTitle}>📊 Venue Dashboard</div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[["🎟️", "Total Bookings", "1,284", "+12%"], ["💰", "Revenue Today", "₹48,200", "+8%"], ["👥", "Check-ins", "892", "Today"], ["⭐", "Avg Rating", "4.8/5", "342 reviews"]].map(([icon, label, val, sub]) => (
          <div key={label} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 16 }}>
            <div style={{ fontSize: 24, marginBottom: 6 }}>{icon}</div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, marginBottom: 4 }}>{label}</div>
            <div style={{ color: "white", fontSize: 20, fontWeight: 800 }}>{val}</div>
            <div style={{ color: "#4ade80", fontSize: 11, marginTop: 2 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {["overview", "bookings", "qrscan", "offers"].map(t => (
          <button key={t} onClick={() => setAdminTab(t)} style={{ ...S.pill(adminTab === t), fontSize: 12 }}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {adminTab === "overview" && (
        <>
          <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 16, marginBottom: 14 }}>
            <div style={{ color: "white", fontWeight: 700, marginBottom: 12 }}>📈 Booking Trend (7 days)</div>
            <div style={{ display: "flex", alignItems: "end", gap: 6, height: 80 }}>
              {[45, 78, 56, 90, 65, 120, 95].map((h, i) => (
                <div key={i} style={{ flex: 1, background: "linear-gradient(180deg, #f59e0b, #ef4444)", borderRadius: "4px 4px 0 0", height: (h / 120 * 80) + "px", transition: "height 0.5s" }} />
              ))}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i} style={{ flex: 1, textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: 10 }}>{d}</div>)}
            </div>
          </div>

          <div style={S.card}>
            <div style={{ padding: 16 }}>
              <div style={{ color: "white", fontWeight: 700, marginBottom: 12 }}>🔥 Recent Bookings</div>
              {tickets.slice(0, 5).concat([
                { id: "AMZ00001234", property: { name: "Demo Venue" }, date: "2026-03-15", tickets: 2, total: 1599 },
                { id: "AMZ00005678", property: { name: "Demo Venue" }, date: "2026-03-14", tickets: 4, total: 3199 },
              ]).slice(0, 5).map(t => (
                <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <div>
                    <div style={{ color: "white", fontSize: 14, fontWeight: 600 }}>{t.id}</div>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>{t.date} · {t.tickets} tickets</div>
                  </div>
                  <div style={{ color: "#f59e0b", fontWeight: 700 }}>₹{t.total}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {adminTab === "qrscan" && renderScan()}

      {adminTab === "offers" && (
        <div>
          <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginBottom: 16 }}>Manage your active coupons and offers</div>
          {["SPLASH20 - 20% off (All bookings)", "NEON15 - 15% off (Weekends)", "GROUP10 - ₹100 off (4+ tickets)"].map(o => (
            <div key={o} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "14px 16px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ color: "white", fontSize: 14 }}>{o}</div>
              <div style={S.tag("#4ade80")}>ACTIVE</div>
            </div>
          ))}
          <button style={S.btn()}>+ Add New Offer</button>
        </div>
      )}
    </div>
  );

  const renderSuperAdminDashboard = () => (
    <div style={{ padding: "20px 20px 80px" }}>
      <div style={S.sectionTitle}>🔮 Super Admin</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[["🏢", "Total Venues", "142", "+3 this week"], ["👥", "Active Users", "89,421", "+1,234 today"], ["💰", "GMV Today", "₹4.8L", "+22%"], ["🎟️", "Tickets Today", "3,892", "+18%"]].map(([icon, label, val, sub]) => (
          <div key={label} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 16 }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{icon}</div>
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>{label}</div>
            <div style={{ color: "white", fontSize: 20, fontWeight: 800 }}>{val}</div>
            <div style={{ color: "#4ade80", fontSize: 11 }}>{sub}</div>
          </div>
        ))}
      </div>

      <div style={S.sectionTitle}>🏢 Venue Management</div>
      {PROPERTIES.map(p => (
        <div key={p.id} style={{ ...S.card, padding: "14px 16px", display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ fontSize: 36 }}>{p.image}</div>
          <div style={{ flex: 1 }}>
            <div style={{ color: "white", fontWeight: 700, fontSize: 15 }}>{p.name}</div>
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>{p.city} · {p.type} · ⭐ {p.rating}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={S.tag("#4ade80")}>LIVE</div>
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, textAlign: "center" }}>{p.available} left</div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderContent = () => {
    if (role === "superadmin") return renderSuperAdminDashboard();
    if (role === "admin") {
      if (activeTab === "scan") return renderScan();
      return renderAdminDashboard();
    }
    switch (activeTab) {
      case "home": return renderHome();
      case "explore": return renderHome(); // same but focused on explore
      case "tickets": return renderTickets();
      case "wallet": return renderWallet();
      case "profile": return renderProfile();
      default: return renderHome();
    }
  };

  if (screen === "booking") return (
    <div style={S.shell}>
      <style>{`* { -webkit-tap-highlight-color: transparent; } input[type=date]::-webkit-calendar-picker-indicator { filter: invert(1); }`}</style>
      {paySuccess ? (
        <div style={{ minHeight: "100vh", background: "#0a0a0f", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ fontSize: 80, marginBottom: 20 }}>🎉</div>
          <div style={{ color: "white", fontSize: 26, fontWeight: 900, textAlign: "center", marginBottom: 8 }}>Booking Confirmed!</div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 14, textAlign: "center", marginBottom: 24 }}>Your ticket is on its way to email & WhatsApp</div>

          {showScratch && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ color: "#f59e0b", fontWeight: 800, textAlign: "center", marginBottom: 12 }}>🎰 You got a Scratch Card!</div>
              <ScratchCard prize={scratchPrize} onReveal={(p) => { addNotification("Scratch card revealed: " + p + "!"); setWallet(w => w + parseInt(p.replace(/\D/g, ""))); }} />
            </div>
          )}

          <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 16, padding: "14px 20px", marginBottom: 20, textAlign: "center" }}>
            <div style={{ color: "#4ade80", fontSize: 16, fontWeight: 700 }}>✅ 5% Cashback Added!</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 14 }}>₹{Math.round(prices.total * 0.05)} added to your wallet</div>
          </div>

          <button onClick={() => { setScreen("app"); setActiveTab("tickets"); setPaySuccess(false); setShowScratch(false); }} style={S.btn()}>
            View My Ticket 🎟️
          </button>
        </div>
      ) : renderBooking()}
    </div>
  );

  return (
    <div style={S.shell}>
      <style>{`* { -webkit-tap-highlight-color: transparent; box-sizing: border-box; } ::-webkit-scrollbar { display: none; } input[type=date]::-webkit-calendar-picker-indicator { filter: invert(1); } @keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* Top Bar */}
      <div style={S.topBar}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 22 }}>🎡</div>
          <div style={{ color: "white", fontSize: 18, fontWeight: 900 }}><span style={{ color: "#f59e0b" }}>a</span>muzing</div>
          {role !== "user" && <div style={S.tag(role === "superadmin" ? "#ef4444" : "#6366f1")}>{role.toUpperCase()}</div>}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {role === "user" && (
            <div style={{ background: "rgba(245,158,11,0.15)", borderRadius: 10, padding: "6px 10px", fontSize: 13, fontWeight: 700, color: "#f59e0b", cursor: "pointer" }} onClick={() => setActiveTab("wallet")}>
              💛 ₹{wallet}
            </div>
          )}
          <div style={{ position: "relative", cursor: "pointer" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg, #f59e0b, #ef4444)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "white" }}>{user?.avatar}</div>
            {notifications.length > 0 && <div style={{ position: "absolute", top: -2, right: -2, width: 10, height: 10, background: "#ef4444", borderRadius: "50%", border: "2px solid #0a0a0f" }} />}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ paddingBottom: 0, overflowY: "auto", maxHeight: "calc(100vh - 120px)" }}>
        {renderContent()}
      </div>

      {/* Bottom Nav */}
      <div style={S.bottomNav}>
        {navItems.map(([icon, label, tab]) => (
          <div key={tab} onClick={() => setActiveTab(tab)} style={S.navItem(activeTab === tab)}>
            <span style={{ fontSize: 22 }}>{icon}</span>
            <span>{label}</span>
          </div>
        ))}
      </div>

      {/* Share Modal */}
      {shareModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={() => setShareModal(false)}>
          <div style={{ background: "#1a1a2e", borderRadius: "24px 24px 0 0", padding: 28, width: "100%", maxWidth: 430, margin: "0 auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ color: "white", fontSize: 20, fontWeight: 800, marginBottom: 8 }}>🎁 Refer & Earn ₹50</div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, marginBottom: 20 }}>Share your code. When your friend books, you both get ₹50!</div>
            <div style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 14, padding: "16px", textAlign: "center", marginBottom: 20 }}>
              <div style={{ color: "#f59e0b", fontSize: 28, fontWeight: 900, letterSpacing: 4 }}>AMZ{user?.phone?.slice(-4)}</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>Your referral code</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[["💬", "WhatsApp", "#25d366"], ["📱", "SMS", "#6366f1"], ["📧", "Email", "#ef4444"], ["🔗", "Copy Link", "#f59e0b"]].map(([i, l, c]) => (
                <button key={l} onClick={() => setShareModal(false)} style={{ background: c + "22", border: `1px solid ${c}44`, borderRadius: 14, padding: "14px", color: "white", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                  {i} {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}